import React, { createRef, FunctionComponent, useEffect, useState } from 'react';
import ReactCodeInput from 'react-code-input';

import { SIGN_UP_STATES } from '@jetslash/market-frontend-shared-core/src/models/user';
import useInterval from '@jetslash/market-frontend-shared-core/src/utilities/useInterval';
import { SignUpPhoneNumberConfirmationFormDefinition } from '@jetslash/market-frontend-shared-core/src/forms/signUpPhoneNumberConfirmation';
import { patchSignUpPhoneNumberConfirmation } from '@jetslash/market-frontend-shared-core/src/api/userApi';
import { ValidationErrorMessages } from '@jetslash/market-frontend-shared-core/src/utilities/form/types';
import { track } from '@/utility/eventTracking/eventTracker';
import { EVENT_SIGN_UP_STEP_VIEWED } from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/events';
import SignUpStepPropGroup from '@jetslash/market-frontend-shared-core/src/utilities/eventTracking/propertyGroupSerializers/SignUpStepPropGroup';
import useFlashMessage from '@/hooks/useFlashMessage';
import { parseErrorMessagesFromResponse } from '@jetslash/market-frontend-shared-core/src/utilities/form/formErrorsFromErrorResponse';
import useSignUpWizard from '@/hooks/useSignUpWizard';
import { ICurrentUser } from '@jetslash/market-frontend-shared-core/src/types/models/user';
import Form from '../fields/Form';
import Reporty from '../../../utility/errorHandling/Reporty';
import FormErrorDisplay from '../FormErrorDisplay';
import CTAButton from '../../design/CTAButton';
import FormContainer from '../FormContainer';
import Col from '../../layout/Col';
import Row from '../../layout/Row';

interface IResendButtonProps {
  loading?: boolean;
  signUpUser: ICurrentUser;
}

const TimedResendPhoneNumberConfirmationButton: FunctionComponent<IResendButtonProps> = ({ signUpUser, loading }) => {
  const { setSignUpUser } = useSignUpWizard();
  const secondsToDisableBetweenRequests = 60;
  const [secondsUntilActive, setSecondsUntilActive] = useState(secondsToDisableBetweenRequests);

  useInterval(() => {
    setSecondsUntilActive(secondsUntilActive - 1);
  }, 1000);

  let buttonDisabled = true;
  let buttonText = `Try again in ${secondsUntilActive} seconds`;

  if (secondsUntilActive < 1) {
    buttonDisabled = false;
    buttonText = "I didn't receive the text";
  }

  const onClick = async () => {
    try {
      setSignUpUser({ ...signUpUser, signUpState: SIGN_UP_STATES.phoneNumberTryAgain }, true);
    } catch (e) {
      Reporty.error(e);
    }
  };

  if (buttonDisabled) {
    return <div style={{ height: 46, lineHeight: '46px' }}>{buttonText}</div>;
  }

  return (
    <CTAButton
      onClick={onClick}
      icon="redo"
      type="default"
      htmlType="button"
      text={buttonText}
      disabled={buttonDisabled || loading}
    />
  );
};

interface ISignUpPhoneNumberConfirmationFormProps {
  onSubmit: Function;
  loading: boolean;
  signUpUser?: ICurrentUser;
  validationErrors?: ValidationErrorMessages;
}

class SignUpPhoneNumberConfirmationForm extends React.Component<ISignUpPhoneNumberConfirmationFormProps> {
  inputRef: any = createRef();

  codeLength: number = 6;

  constructor(props: ISignUpPhoneNumberConfirmationFormProps) {
    super(props);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleOnInvalidCode = this.handleOnInvalidCode.bind(this);
    this.clearCodeInput = this.clearCodeInput.bind(this);
  }

  handleOnInvalidCode() {
    try {
      const { current }: any = this.inputRef;
      if (current) {
        this.clearCodeInput();
      }
    } catch (e) {
      Reporty.error(e);
    }
  }

  handleOnChange(val) {
    const { onSubmit } = this.props;

    if (val && val.length === 6) {
      // this.handleOnInvalidCode()
      onSubmit({ token: val }, this.handleOnInvalidCode);
    }
  }

  clearCodeInput() {
    const { current }: any = this.inputRef;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < current.textInput.length; i++) {
      current.state.input[i] = '';
      current.textInput[i].value = '';
    }
    // Requires a timeout for focus to activate properly on the ref
    setTimeout(() => {
      current.textInput[0]?.focus();
    }, 300);
  }

  render() {
    const { loading, validationErrors } = this.props;

    return (
      <FormContainer>
        <Row>
          <Col span={24}>
            <FormErrorDisplay errors={validationErrors} />
          </Col>
        </Row>
        <Form>
          <Row>
            <Col span={24} textCenter>
              <div style={{ marginTop: 10, marginBottom: 20 }}>
                <ReactCodeInput
                  ref={this.inputRef}
                  name="sms-confirmation"
                  onChange={this.handleOnChange}
                  inputMode="numeric"
                  pattern="^[0-9]*$"
                  disabled={loading}
                  type="text"
                  fields={this.codeLength}
                />
              </div>
            </Col>
          </Row>
        </Form>
      </FormContainer>
    );
  }
}

interface IProps {
  loading: boolean;
  signUpUser: ICurrentUser;
  errors?: ValidationErrorMessages;
}

const SignUpPhoneNumberConfirmationFormContainer: FunctionComponent<IProps> = ({ loading, signUpUser, errors }) => {
  const formDefinition = new SignUpPhoneNumberConfirmationFormDefinition();
  const { signUpStepStartRequest, signUpStepSuccess, signUpStepFailure, updateSignUpUser, setSignUpUser } =
    useSignUpWizard();
  const { addFlashMessage } = useFlashMessage();
  // Set keepLoading to true so that the loading indicator stays active upon success

  useEffect(() => {
    track(
      EVENT_SIGN_UP_STEP_VIEWED,
      SignUpStepPropGroup.create(signUpUser, SignUpStepPropGroup.signUpSteps.SMS_CONFIRMATION_ENTRY),
    );
  }, []);

  const onSignUpPhoneNumberConfirmationSubmit = async ({ token }, handleError) => {
    const signUpPhoneNumberConfirmationData = {
      token,
    };

    signUpStepStartRequest();

    try {
      const response = await patchSignUpPhoneNumberConfirmation(signUpPhoneNumberConfirmationData);
      signUpStepSuccess(null, true);
      setTimeout(() => {
        if (response.data?.userReturnTo) {
          // @ts-ignore
          window.location = response.data.userReturnTo;
        } else {
          window.location.reload();
        }
      }, 800);
    } catch (err) {
      const formErrors = parseErrorMessagesFromResponse(err, formDefinition.HUMANIZATION_MAP);
      if (handleError) {
        handleError();
      }
      signUpStepFailure(formErrors);
      if (err && err.status === 403) {
        addFlashMessage('error', 'You have exceeded the maximum number of guesses for this code.');
        setSignUpUser({ ...signUpUser, signUpState: SIGN_UP_STATES.phoneNumberTryAgain }, true);
      }
    }
  };

  useEffect(() => {
    updateSignUpUser();
  }, []);

  useInterval(() => {
    updateSignUpUser();
  }, 5000);

  return (
    <div style={{ marginLeft: 5, marginRight: 5 }}>
      <Row>
        <Col span={24} textLeft style={{ marginLeft: 5, marginRight: 5, marginBottom: 10 }}>
          Enter the code or click the link sent to <span className="strong">{signUpUser.unconfirmedPhone}</span>.
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SignUpPhoneNumberConfirmationForm
            onSubmit={onSignUpPhoneNumberConfirmationSubmit}
            loading={loading}
            signUpUser={signUpUser}
            validationErrors={errors}
          />
        </Col>
      </Row>
      <Row style={{ marginLeft: 5, marginRight: 5, marginTop: 10 }}>
        <Col span={24} textCenter>
          <TimedResendPhoneNumberConfirmationButton signUpUser={signUpUser} loading={loading} />
        </Col>
      </Row>
    </div>
  );
};

export default SignUpPhoneNumberConfirmationFormContainer;
